/** Confirmacion tactil con animacion, foco gestionado y Escape. */
import { useEffect, useId, useRef } from 'react';
import { MobileIcon } from './MobileIcon';

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  danger = false,
  busy = false,
  reason,
  fields,
  onConfirm,
  onCancel,
  error: dialogError,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  /** Motivo opcional pedido dentro del diálogo (acciones con requires_reason). */
  reason?: {
    label: string;
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    error?: string;
  };
  /** Datos del documento que la acción captura (data_fields del servidor). */
  fields?: Array<{
    name: string;
    label: string;
    required: boolean;
    value: string;
    options?: Array<{ value: string; label: string }>;
    onChange: (next: string) => void;
    error?: string;
  }>;
  /** Error de la última ejecución fallida (queda visible dentro del diálogo). */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const messageId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<Element | null>(null);
  // El cierre se guarda en ref: el efecto de montaje corre una sola vez y los
  // re-renders del padre (p. ej. al escribir el motivo) no roban el foco ni
  // re-bloquean el scroll. Sin esto, cada tecla devolvía el foco al botón
  // Cancelar y el espacio lo activaba cerrando el diálogo.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    previousFocus.current = document.activeElement;
    cancelRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancelRef.current();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
      if (previousFocus.current instanceof HTMLElement) previousFocus.current.focus();
    };
    // Solo al montar/desmontar: el cierre viaja por ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      aria-describedby={messageId}
      className="fixed inset-0 z-50 flex items-end justify-center p-4 safe-area-bottom"
      onClick={() => onCancelRef.current()}
    >
      <div className="absolute inset-0 bg-bg-overlay" />

      <div
        className="relative w-full max-w-md rounded-t-card bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-2 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-border-dark" />

        <div className="mb-3 mt-2 flex items-center gap-3">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
              danger ? 'bg-error-light' : 'bg-primary-light'
            }`}
          >
            <MobileIcon
              name={danger ? 'warning' : 'check_circle'}
              className={`h-6 w-6 ${danger ? 'text-error' : 'text-primary'}`}
            />
          </div>
          <p className="min-w-0 flex-1 break-words text-lg font-semibold text-text-primary">{title}</p>
        </div>

        <p id={messageId} className="mb-5 break-words text-sm text-text-secondary">
          {message}
        </p>

        {reason && (
          <div className="mb-5">
            <label
              htmlFor={`${messageId}-reason`}
              className="mb-1.5 block break-words text-sm font-medium text-text-secondary"
            >
              {reason.label}
              <span aria-hidden="true" className="text-error"> *</span>
            </label>
            <textarea
              id={`${messageId}-reason`}
              rows={3}
              value={reason.value}
              onChange={(e) => reason.onChange(e.target.value)}
              placeholder={reason.placeholder ?? 'Describe el motivo...'}
              aria-invalid={reason.error ? true : undefined}
              aria-describedby={reason.error ? `${messageId}-reason-error` : undefined}
              className="min-h-[52px] w-full rounded-xl border-[1.5px] border-border bg-white px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none"
            />
            {reason.error && (
              <p id={`${messageId}-reason-error`} className="mt-1.5 break-words text-sm text-error">
                {reason.error}
              </p>
            )}
          </div>
        )}

        {(fields ?? []).map((field) => {
          const fieldId = `${messageId}-${field.name}`;
          const fieldErrorId = `${fieldId}-error`;
          return (
            <div key={field.name} className="mb-5">
              <label
                htmlFor={fieldId}
                className="mb-1.5 block break-words text-sm font-medium text-text-secondary"
              >
                {field.label}
                {field.required && (
                  <span aria-hidden="true" className="text-error"> *</span>
                )}
              </label>
              {(field.options ?? []).length > 0 ? (
                <select
                  id={fieldId}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  aria-invalid={field.error ? true : undefined}
                  aria-describedby={field.error ? fieldErrorId : undefined}
                  className="h-12 w-full rounded-xl border-[1.5px] border-border bg-white px-3 text-sm text-text-primary focus:border-primary focus:outline-none"
                >
                  <option value="">Seleccionar...</option>
                  {(field.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={fieldId}
                  type="text"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  aria-invalid={field.error ? true : undefined}
                  aria-describedby={field.error ? fieldErrorId : undefined}
                  className="h-12 w-full rounded-xl border-[1.5px] border-border bg-white px-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none"
                />
              )}
              {field.error && (
                <p id={fieldErrorId} className="mt-1.5 break-words text-sm text-error">
                  {field.error}
                </p>
              )}
            </div>
          );
        })}

        {dialogError && (
          <div className="mb-4 flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3" role="alert">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{dialogError}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => onCancelRef.current()}
            className="min-h-[48px] rounded-full bg-bg-tertiary font-semibold text-text-secondary transition-colors hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`min-h-[48px] rounded-full font-semibold text-white transition-all disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.97] ${
              danger ? 'bg-error hover:bg-error-dark' : 'bg-primary hover:bg-primary-hover'
            }`}
          >
            {busy ? 'Enviando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
